import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useProfession } from "../lib/professions";
import { UnitSwitcher, ProfessionOnboarding } from "./Units";
import { LogoMark } from "./Logo";
import {
  House, CurrencyDollar, ChartLine, Package, Users, UserCircle, Tooth,
  FileText, GearSix, SignOut, List, X, Receipt, Crown, ShieldCheck, Flask, ChartBar,
} from "@phosphor-icons/react";

const RECEPCAO_NAV = ["/app", "/app/procedures", "/app/quotes"];
const GESTOR_HIDDEN = ["/app/settings", "/app/billing", "/app/admin"];
const NAV = [
  { to: "/app", label: "Dashboard", icon: House, end: true },
  { to: "/app/fixed-costs", label: "Custos Fixos", icon: CurrencyDollar },
  { to: "/app/variable-costs", label: "Custos Variáveis", icon: ChartLine },
  { to: "/app/materials", label: "Materiais", icon: Package },
  { to: "/app/employees", label: "Funcionários", icon: Users },
  { to: "/app/professionals", label: "Profissionais", icon: UserCircle },
  { to: "/app/procedures", label: "Procedimentos", icon: Tooth },
  { to: "/app/quotes", label: "Orçamentos", icon: Receipt },
  { to: "/app/simulator", label: "Simulador", icon: Flask },
  { to: "/app/reports", label: "Relatórios", icon: ChartBar },
  { to: "/app/settings", label: "Configurações", icon: GearSix },
  { to: "/app/billing", label: "Meu Plano", icon: Crown },
  { to: "/app/admin", label: "Painel do Dono", icon: ShieldCheck, ownerOnly: true },
];

function PlanBanner({ billing }) {
  if (!billing || billing.is_owner) return null;
  if (billing.read_only) {
    return (
      <div className="bg-rose-600 text-white text-sm px-4 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="plan-banner-expired">
        <span>Seu período de acesso terminou. O sistema está em modo leitura.</span>
        <Link to="/app/billing" className="underline font-semibold">Assinar um plano</Link>
      </div>
    );
  }
  if (billing.status === "trial") {
    return (
      <div className="bg-amber-50 text-amber-900 border-b border-amber-200 text-sm px-4 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="plan-banner-trial">
        <span>Teste grátis do plano Profissional: faltam <strong>{billing.days_left}</strong> dia(s).</span>
        <Link to="/app/billing" className="underline font-semibold">Ver planos</Link>
      </div>
    );
  }
  if (billing.status === "past_due") {
    return (
      <div className="bg-rose-50 text-rose-800 border-b border-rose-200 text-sm px-4 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="plan-banner-pastdue">
        <span>Não conseguimos processar seu pagamento. Atualize o cartão para evitar bloqueio.</span>
        <Link to="/app/billing" className="underline font-semibold">Resolver</Link>
      </div>
    );
  }
  return null;
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const prof = useProfession();
  const role = user?.team_role;
  const path = location.pathname.replace(/\/$/, "");
  const blocked = (role === "recepcao" && !RECEPCAO_NAV.includes(path) && !path.startsWith("/app/quotes") && !path.startsWith("/app/procedures"))
    || (role === "gestor" && GESTOR_HIDDEN.some((p) => path.startsWith(p)));
  if (blocked) return <Navigate to="/app" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static top-0 left-0 z-40 h-screen w-64 bg-white border-r border-slate-200 transition-transform duration-200 flex flex-col`}
      >
        <div className="h-16 px-6 flex items-center justify-between border-b border-slate-200">
          <div className="flex items-center gap-2 min-w-0">
            <LogoMark size={34} className="shrink-0" />
            <div className="min-w-0">
              <div className="font-heading leading-tight truncate text-base"><span className="font-semibold text-indigo-600">Seu</span> <span className="font-extrabold text-slate-900">Precificador</span></div>
              <div className="text-[10px] font-semibold uppercase tracking-wider truncate flex items-center gap-1" style={{ color: prof.color }} data-testid="brand-profession"><prof.icon size={11} weight="fill" />{prof.label}</div>
            </div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {NAV.filter((n) => (!n.ownerOnly || user?.billing?.is_owner) && (user?.team_role !== "recepcao" || RECEPCAO_NAV.includes(n.to)) && (user?.team_role !== "gestor" || !GESTOR_HIDDEN.includes(n.to))).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => setOpen(false)}
              style={({ isActive }) => (isActive ? { backgroundColor: `rgba(var(--brand-rgb), 0.10)`, color: "var(--brand)" } : undefined)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition ${
                  isActive
                    ? ""
                    : n.highlight ? "text-teal-700 hover:bg-teal-50" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              {n.to === "/app/procedures" ? <prof.icon size={18} weight="duotone" /> : <n.icon size={18} weight="duotone" />}
              {n.to === "/app/procedures" ? prof.procedures : n.label}
              {n.highlight && <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Novo</span>}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <div className="px-3 py-2 text-xs text-slate-500 truncate">
            {user?.team_member ? <><div className="truncate">{user.team_member.name} · {user.team_member.email}</div><div className="text-indigo-600 font-medium">{user.team_role === "recepcao" ? "Recepção" : "Gestor"} · {user.clinic_name || user.name}</div></> : `${user?.name} · ${user?.email}`}
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 rounded-md hover:bg-slate-50"
          >
            <SignOut size={16} /> Sair
          </button>
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-20 flex items-center px-4 lg:px-8 gap-4">
          <button
            className="lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="sidebar-open"
          >
            <List size={22} />
          </button>
          <div className="flex-1" />
          <UnitSwitcher />
          <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-white" style={{ backgroundColor: prof.color }} data-testid="header-profession-badge"><prof.icon size={13} weight="fill" />{prof.label}</span>
            <span data-testid="header-clinic-name">{user?.clinic_name || `Minha ${prof.place}`}</span>
          </div>
        </header>
        <PlanBanner billing={user?.billing} />
        <ProfessionOnboarding />
        <main className="flex-1 p-4 lg:p-8">
          <div className="fade-in max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
